import React, { useEffect, useState } from 'react';
import { Button, TextField, Spinner, Text, Flex, DataList, Select } from '@radix-ui/themes';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../../api';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateSchoolAdminSchool } from './../../../redux/slices/schoolAdminAccountSlice';

const SchoolDetailsForm = ({ setIsEditing }) => {
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [regions, setRegions] = useState([]);
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state.user.details);
    const schoolDetails = useSelector((state) => state.schoolAdmin.school);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await api.get('/countries');
                setCountries(response.data);
            } catch (error) {
                console.error('Error fetching countries:', error);
                toast.error('Failed to load countries', { position: 'bottom-right' });
            }
        };
        fetchCountries();
    }, []);



    const formik = useFormik({
        initialValues: {
            name: schoolDetails.name || '',
            sector: schoolDetails.sector || '',
            email: schoolDetails.email || '',
            phone: schoolDetails.phone || '',
            country_id: String(schoolDetails.country_id) || '',
            region_id: String(schoolDetails.region_id) || '',
            location: schoolDetails.location || '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('School name is required'),
            sector: Yup.string().required('School sector is required'),
            email: Yup.string().email('Invalid email').notRequired(),
            phone: Yup.string()
                .matches(/^[0-9]{7,10}$/, 'Phone number must be between 7 and 10 digits')
                .notRequired(),
            country_id: Yup.string().required('Country is required'),
            region_id: Yup.string().required('Region is required'),
            location: Yup.string().notRequired(),
        }),
        onSubmit: async (values) => {
            try {
                formik.setStatus('');
                setLoading(true);
                const schoolData = {
                    name: values.name,
                    sector: values.sector,
                    email: values.email,
                    phone: values.phone,
                    country_id: values.country_id,
                    region_id: values.region_id,
                    location: values.location,
                };

                const response = await api.put(`/schools/details/${schoolDetails.id}`, schoolData);
                const data = response.data;

                if (data.errors) {
                    formik.setErrors(data.errors);
                } else {
                    dispatch(updateSchoolAdminSchool(data.school));
                    toast.success(data.message, { position: 'bottom-right' });
                    setIsEditing(false);
                }
            } catch (err) {
                console.log('check errors::', err.response?.data?.errors);
                if (err.response?.data?.errors) {
                    formik.setErrors(err.response.data.errors);
                }
                formik.setStatus('Update failed. Please try again.');
                toast.error('Update failed. Please try again.', { position: 'bottom-right' });
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        console.log('country chagned:', formik.values.country_id);
        if (formik.values.country_id) {
            const fetchRegions = async () => {
                try {
                    const response = await api.get(`/regions/${formik.values.country_id}`);
                    setRegions(response.data);
                } catch (error) {
                    console.error('Error fetching regions:', error);
                    toast.error('Failed to load regions', { position: 'bottom-right' });
                }
            };
            fetchRegions();
        } else {
            setRegions([]); // Clear regions if no country_id
        }
    }, [formik.values.country_id]);

    // useEffect(() => {
    //     console.log('school-details full object:', schoolDetails);
    //     console.log('school-details country_id:', typeof String(schoolDetails.country_id));
    //     console.log('school-details region_id:', typeof schoolDetails?.region_id);
    //     // if (schoolDetails) {
    //     formik.setValues({
    //         name: schoolDetails.name || '',
    //         sector: schoolDetails.sector || '',
    //         email: schoolDetails.email || '',
    //         phone: schoolDetails.phone || '',
    //         country_id: String(schoolDetails.country_id) || '',
    //         region_id: String(schoolDetails.region_id) || '',
    //     });
    //     // }
    // }, [schoolDetails]);

    return (
        <form onSubmit={formik.handleSubmit}>
            {formik.status && (
                <Flex direction="column" justify="center" align="center">
                    <Text color="red" mb="4">{formik.status}</Text>
                </Flex>
            )}
            <DataList.Root>
                <DataList.Item>
                    <DataList.Label minWidth="100px">School Name</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="1">
                            <TextField.Root
                                size="3"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="School Name"
                            />
                            {formik.touched.name && formik.errors.name && (
                                <Text color="red" size="2">{formik.errors.name}</Text>
                            )}
                        </Flex>
                    </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                    <DataList.Label minWidth="100px">Sector</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="1">
                            <Select.Root
                                name="sector"
                                value={formik.values.sector}
                                onValueChange={(value) => formik.setFieldValue('sector', value)}
                            >
                                <Select.Trigger placeholder="Select sector">
                                    {formik.values.sector || 'Select sector'}
                                </Select.Trigger>
                                <Select.Content>
                                    <Select.Item value="private">Private</Select.Item>
                                    <Select.Item value="public">Public</Select.Item>
                                </Select.Content>
                            </Select.Root>
                            {formik.touched.sector && formik.errors.sector && (
                                <Text color="red" size="2">{formik.errors.sector}</Text>
                            )}
                        </Flex>
                    </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                    <DataList.Label minWidth="100px">Phone Number</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="1">
                            <TextField.Root
                                size="3"
                                name="phone"
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Phone Number"
                            />
                            {formik.touched.phone && formik.errors.phone && (
                                <Text color="red" size="2">{formik.errors.phone}</Text>
                            )}
                        </Flex>
                    </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                    <DataList.Label minWidth="100px">Email</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="1">
                            <TextField.Root
                                size="3"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Email Address"
                            />
                            {formik.touched.email && formik.errors.email && (
                                <Text color="red" size="2">{formik.errors.email}</Text>
                            )}
                        </Flex>
                    </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                    <DataList.Label minWidth="100px">Country</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="1">
                            <Select.Root
                                name="country_id"
                                value={formik.values.country_id}
                                onValueChange={(value) => {
                                    formik.setFieldValue('country_id', value);
                                    formik.setFieldValue('region_id', '');
                                    setRegions([]);
                                }}
                            >
                                <Select.Trigger placeholder="Select country">
                                    {formik.values.country_id
                                        ? countries.find((country) => country.id == formik.values.country_id)?.name ||
                                        'Select country'
                                        : 'Select country'}
                                </Select.Trigger>
                                <Select.Content>
                                    {countries.map((country) => (
                                        <Select.Item key={country.id} value={country.id}>
                                            {country.name}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Root>
                            {formik.touched.country_id && formik.errors.country_id && (
                                <Text color="red" size="2">{formik.errors.country_id}</Text>
                            )}
                        </Flex>
                    </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                    <DataList.Label minWidth="100px">Region</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="1">
                            <Select.Root
                                name="region_id"
                                value={formik.values.region_id}
                                onValueChange={(value) => formik.setFieldValue('region_id', value)}
                                disabled={!formik.values.country_id}
                            >
                                <Select.Trigger placeholder="Select region">
                                    {formik.values.region_id
                                        ? regions.find((region) => region.id == formik.values.region_id)?.name ||
                                        'Select region'
                                        : 'Select region'}
                                </Select.Trigger>
                                <Select.Content>
                                    {regions.map((region) => (
                                        <Select.Item key={region.id} value={region.id}>
                                            {region.name}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Root>
                            {formik.touched.region_id && formik.errors.region_id && (
                                <Text color="red" size="2">{formik.errors.region_id}</Text>
                            )}
                        </Flex>
                    </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                    <DataList.Label minWidth="100px">Location</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="1">
                            <TextField.Root
                                size="3"
                                name="location"
                                value={formik.values.location}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="School Location"
                            />
                            {formik.touched.location && formik.errors.location && (
                                <Text color="red" size="2">{formik.errors.location}</Text>
                            )}
                        </Flex>
                    </DataList.Value>
                </DataList.Item>


            </DataList.Root>
            <Flex direction="row" justify="between" align="center">
                <Button disabled={loading}>
                    {loading ? (
                        <Flex gap="2" align="center">
                            <Spinner size="1" />
                            Saving...
                        </Flex>
                    ) : (
                        'Save'
                    )}
                </Button>
            </Flex>
        </form>
    );
};

export default SchoolDetailsForm;